/**
 * ESCALA FLUIDA — Alfredo Central
 * ==============================================================================
 *
 * O problema que este arquivo resolve
 * -----------------------------------
 * A Central foi desenhada num monitor de 1440px e todo tamanho foi escrito em
 * px fixo. Medido no gabarito visual (`_gabarito-visual/antes/medidas.md`): a
 * fonte raiz, o corpo, o censo tipográfico da tela de login e a altura de todo
 * botão saem IDÊNTICOS em 1366, 1440 e 1920. Nada escala.
 *
 * Consequência num monitor de loja (1366x768, o mais comum): o mesmo layout
 * ocupa proporcionalmente 5,4% mais tela do que ocupa em 1440 — a barra lateral
 * de 400px come 29% da largura, o botão "Detalhes" e os botões de pergunta da
 * IA saem truncados. Num monitor de 1920 acontece o contrário: o texto de 16px
 * fica pequeno dentro de caixas que esticaram em %.
 *
 * A ideia: um "pixel fluido" (--u)
 * --------------------------------
 * Em vez de dar nome a cada tamanho e recalcular cada um, define-se UMA unidade
 * — `--u`, que vale exatamente 1px quando a janela tem 1440px — e todo tamanho
 * passa a ser `calc(var(--u) * N)`, onde N é o MESMO número que já estava no
 * código. Isso tem duas consequências que importam:
 *
 *   1. Em 1440px o resultado é aritmeticamente idêntico ao de hoje. O refactor
 *      não pode mudar o visual no monitor do Guilherme, e não muda.
 *   2. A migração é mecânica e auditável: `14px` vira `calc(var(--u) * 14)`.
 *      O número nunca muda; muda só a FORMA de expressá-lo.
 *
 * A fórmula, e por que estes limites
 * ----------------------------------
 *   --u: clamp(0.05rem, min(0.0175rem + 0.05vw, 0.04rem + 0.025vw), 0.075rem)
 *
 * São DUAS retas que se cruzam exatamente em 1440px, e o `min()` escolhe a
 * menor das duas — o que faz a de baixo valer abaixo de 1440 e a de cima valer
 * acima. Um `clamp()` sozinho só sabe uma reta; aqui é preciso encolher rápido
 * e crescer devagar, que são inclinações diferentes:
 *
 *   · reta de baixo  0.0175rem + 0.05vw   → inclinação 0,72 (quase proporcional)
 *     Abaixo de 1440 o objetivo é NÃO ESTOURAR. Encolher quase junto com a
 *     janela é o que devolve as proporções do desenho original num monitor
 *     menor. Em 1440: 0,28px + 0,05 x 14,40 = 0,28 + 0,72 = 1,00px exato.
 *
 *   · reta de cima   0.04rem + 0.025vw    → inclinação 0,36 (amortecida)
 *     Acima de 1440 crescer proporcionalmente daria 21,3px de corpo num
 *     monitor de 1920 — tamanho de outdoor. Amortecido dá 17,9px, que é a
 *     leitura confortável que a tela grande pede.
 *     Em 1440: 0,64px + 0,025 x 14,40 = 0,64 + 0,36 = 1,00px exato.
 *
 *   · piso 0.05rem (0,8x)   congela abaixo de ~1040px de largura. Não faz
 *     sentido continuar encolhendo texto para caber num layout que já vai
 *     precisar de rolagem de qualquer jeito.
 *   · teto 0.075rem (1,2x)  congela acima de ~2240px. Impede que um monitor 4K
 *     transforme a Central em cartaz.
 *
 * Os coeficientes NÃO são arredondados por estética: 0,05vw = 72/1440 e
 * 0,025vw = 36/1440 são divisões exatas em decimal, e as constantes em rem
 * (0,0175 x 16 = 0,28 e 0,04 x 16 = 0,64) também. É isso que garante que a soma
 * em 1440 seja 1,000000 e não 0,999997 — a diferença entre "pixel idêntico" e
 * "pixel quase idêntico".
 *
 * Medido no Brave headless, para os 26 tamanhos que existem no código
 * (8px a 100px), nas larguras 1024 / 1152 / 1280 / 1366 / 1440 / 1600 / 1920 /
 * 2011 / 2560:
 *
 *   | largura | fator | 11px  | 14px  | 16px  | 20px  | 24px  |
 *   |---------|-------|-------|-------|-------|-------|-------|
 *   | 1024    | 0,800 |  8,80 | 11,20 | 12,80 | 16,00 | 19,20 |
 *   | 1280    | 0,920 | 10,12 | 12,88 | 14,72 | 18,40 | 22,08 |
 *   | 1366    | 0,963 | 10,59 | 13,48 | 15,41 | 19,26 | 23,11 |
 *   | 1440    | 1,000 | 11,00 | 14,00 | 16,00 | 20,00 | 24,00 |  <- exato
 *   | 1600    | 1,040 | 11,44 | 14,56 | 16,64 | 20,80 | 24,96 |
 *   | 1920    | 1,120 | 12,32 | 15,68 | 17,92 | 22,40 | 26,88 |
 *   | 2560    | 1,200 | 13,20 | 16,80 | 19,20 | 24,00 | 28,80 |
 *
 * Por que rem e não px na fórmula
 * -------------------------------
 * As constantes e os limites estão em `rem` para que quem aumenta a fonte no
 * navegador (acessibilidade) continue sendo atendido: o piso e o teto sobem
 * junto com a preferência do usuário. Com a raiz no padrão de 16px o resultado
 * é numericamente idêntico ao da versão em px.
 *
 * O que NÃO passa por aqui
 * ------------------------
 * Borda de 1px, largura de traço de SVG e as condições dos `@media` continuam
 * fixas — são coisas que devem mesmo ser fixas. Cor, raio e sombra também não
 * são assunto deste arquivo: aqui só se muda a forma de expressar TAMANHO.
 *
 * ARMADILHA: não mexer no font-size da raiz (html)
 * ------------------------------------------------
 * A fórmula do `--u` é escrita em `rem`, ou seja, ela LÊ o font-size da raiz.
 * Se alguém tornar o `html { font-size: ... }` fluido também, a escala passa a
 * se alimentar de si mesma e o resultado deixa de bater com o gabarito — e o
 * erro composto cresce nas pontas, onde justamente ninguém está olhando. A raiz
 * fica no padrão do navegador; quem escala é o corpo (`body { font-size:
 * var(--fs-base) }` em global.ts) e cada tamanho declarado.
 */

/** Largura em que a escala vale exatamente 1 — o monitor em que a Central foi desenhada. */
export const LARGURA_ANCORA = 1440;

/**
 * O pixel fluido. Vale 1px em 1440px de janela; 0,963px em 1366; 1,12px em 1920.
 * Ver o cabeçalho deste arquivo para a dedução dos coeficientes.
 */
export const UNIDADE_FLUIDA =
  "clamp(0.05rem, min(0.0175rem + 0.05vw, 0.04rem + 0.025vw), 0.075rem)";

/**
 * Converte um tamanho que hoje está em px para a escala fluida, preservando o
 * número. `esc(14)` devolve exatamente 14px em 1440 e acompanha a janela fora
 * disso. Usar quando o valor não está na escada nomeada abaixo.
 */
export const esc = (px: number): string => `calc(var(--u) * ${px})`;

/**
 * ESCADA TIPOGRÁFICA — os tamanhos que a Central realmente usa hoje, cada um
 * com um nome. O número é o valor em 1440px, então o nome documenta o valor.
 * Levantado do censo do código (`font-size` em px), não inventado:
 * 14px aparece 150x, 20px 58x, 12px 43x, 24px 32x, 16px 32x.
 */
export const TIPOGRAFIA_PX = {
  "3xs": 10,
  "2xs": 11,
  xs: 12,
  sm: 13,
  md: 14,
  base: 16,
  lg: 18,
  xl: 20,
  "2xl": 24,
  "3xl": 30,
  "4xl": 32,
  "5xl": 40,
  "6xl": 48,
  "7xl": 64,
} as const;

/**
 * ESCADA DE ESPAÇAMENTO — os valores de padding/margin/gap que já existem no
 * código, nomeados pelo valor em 1440px. Censo: 8px 227x, 12px 169x, 16px 153x,
 * 10px 128x, 20px 110x, 4px 82x, 24px 82x.
 */
export const ESPACAMENTO_PX = [
  2, 4, 5, 6, 8, 10, 12, 15, 16, 20, 24, 32, 40,
] as const;

type ChaveTipografia = keyof typeof TIPOGRAFIA_PX;

/** `fs.md` → `var(--fs-md)`, para usar dentro dos styled-components. */
export const fs = Object.fromEntries(
  Object.keys(TIPOGRAFIA_PX).map((nome) => [nome, `var(--fs-${nome})`]),
) as Record<ChaveTipografia, string>;

/** `sp[8]` → `var(--sp-8)`. */
export const sp = Object.fromEntries(
  ESPACAMENTO_PX.map((valor) => [valor, `var(--sp-${valor})`]),
) as Record<(typeof ESPACAMENTO_PX)[number], string>;

/**
 * O bloco `:root` com a unidade e as duas escadas. É interpolado dentro do
 * GlobalStyles (`global.ts`) em vez de virar um segundo `createGlobalStyle`:
 * um único estilo global significa que não existe pergunta de ordem de
 * injeção, e a unidade está garantidamente presente em toda tela que renderiza
 * o GlobalStyles — que é o app inteiro.
 */
export const raizEscala = `
:root {
  --u: ${UNIDADE_FLUIDA};

  /* escada tipográfica */
${Object.entries(TIPOGRAFIA_PX)
  .map(([nome, px]) => `  --fs-${nome}: calc(var(--u) * ${px});`)
  .join("\n")}

  /* escada de espaçamento */
${ESPACAMENTO_PX.map((px) => `  --sp-${px}: calc(var(--u) * ${px});`).join("\n")}
}
`;
